import React, { useState } from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';

// A Conductor-style mini-nav pinned to the chat's top-right corner: one thin bar
// per question the user has asked. Hovering the nav reveals a list of those
// questions; clicking a bar or a row scrolls the transcript to that message.
//
// Purely presentational — the caller owns the transcript scroll and passes the
// user messages plus an onSelect that jumps to one.

const Root = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-start;
  gap: 6px;
`;

// The stack of bars. row-reverse on Root keeps the bars flush to the right edge
// with the flyout list opening to their left.
const Bars = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  /* Cap the height on very long conversations so the bars never run off the
     window; the list flyout stays the complete index. */
  max-height: 40%;
  overflow: hidden;
`;

const Bar = styled.button<{ $active: boolean }>`
  width: 18px;
  height: 3px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background-color: ${(props) =>
    props.$active ? 'var(--color-ai-accent)' : 'var(--color-border-secondary)'};
  transition:
    background-color 0.12s ease,
    width 0.12s ease;

  &:hover {
    width: 24px;
    background-color: var(--color-ai-accent);
  }
`;

const Flyout = styled.div`
  max-width: 260px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-floating-button);
`;

const Row = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 8px;
  border: none;
  border-radius: 8px;
  background: none;
  cursor: pointer;

  &:hover {
    background-color: var(--color-bg-tertiary);
  }
`;

// One line of question text, clipped so a long question can't blow out the panel.
const RowText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InsightsMessageNav = ({
  messages,
  onSelect,
}: {
  messages: { id: string; content: string }[];
  onSelect: (id: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);

  if (messages.length === 0) {
    return null;
  }

  const select = (id: string) => {
    setActiveId(id);
    onSelect(id);
  };

  return (
    <Root
      data-testid="insights-message-nav"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Bars>
        {messages.map((message, index) => (
          <Bar
            key={message.id}
            type="button"
            $active={message.id === activeId}
            onClick={() => select(message.id)}
            aria-label={`Jump to question ${index + 1}`}
            data-testid={`insights-message-nav-bar-${index}`}
          />
        ))}
      </Bars>
      {isOpen && (
        <Flyout data-testid="insights-message-nav-list">
          {messages.map((message, index) => (
            <Row
              key={message.id}
              type="button"
              onClick={() => select(message.id)}
              data-testid={`insights-message-nav-row-${index}`}
            >
              <RowText>
                <Typography.Body
                  type="Body 12"
                  color="var(--color-text-primary)"
                >
                  {message.content}
                </Typography.Body>
              </RowText>
            </Row>
          ))}
        </Flyout>
      )}
    </Root>
  );
};

export default InsightsMessageNav;
